import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useTheme } from '../contexts/ThemeContext';

const Testimonials = () => {
  const sectionRef = useRef(null);
  const testimonialsRef = useRef(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { isDark } = useTheme();

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "CEO, TechStart Inc.",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      content: "Codepact transformed our digital presence completely. Their expertise in web development and cloud solutions helped us scale our business by 300% in just one year. The team's professionalism and technical excellence are unmatched.",
      rating: 5
    },
    {
      name: "Michael Chen",
      role: "CTO, InnovateCorp",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      content: "Working with Codepact was a game-changer for our mobile app development. They delivered a flawless React Native application that exceeded all our expectations. Their attention to detail and user experience focus is remarkable.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Marketing Director, GrowthCo",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      content: "The UI/UX design work Codepact did for our platform was outstanding. They understood our brand perfectly and created an intuitive interface that our users love. Our conversion rates increased by 150% after the redesign.",
      rating: 5
    },
    {
      name: "David Thompson",
      role: "Founder, DataFlow Solutions",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      content: "Codepact's IT consulting services helped us modernize our entire infrastructure. Their cloud migration strategy was flawless, and we've seen significant cost savings while improving performance. Highly recommended!",
      rating: 5
    },
    {
      name: "Lisa Wang",
      role: "Product Manager, NextGen Apps",
      image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
      content: "The AI-powered analytics dashboard Codepact built for us has revolutionized how we make business decisions. The insights are incredibly valuable, and the interface is so intuitive that our entire team adopted it immediately.",
      rating: 5
    }
  ];

  useEffect(() => {
    const section = sectionRef.current;
    const testimonials = testimonialsRef.current;

    // Ensure refs are available before animating
    if (!section || !testimonials) {
      return;
    }

    // Section entrance animation
    const sectionHeader = section.querySelector('.section-header');
    if (sectionHeader) {
      gsap.fromTo(sectionHeader,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: section,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }

    // Testimonials animation
    gsap.fromTo(testimonials,
      { y: 80, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: testimonials,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

  }, []);

  useEffect(() => {
    // Auto-rotate testimonials
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <svg
        key={index}
        className={`w-5 h-5 ${index < rating ? 'text-yellow-400' : 'text-gray-300'}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  return (
    <section ref={sectionRef} className="py-14 bg-white dark:bg-gray-900 relative">
      {/* Section Separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-300 dark:via-primary-600 to-transparent"></div>
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Section Header - Left Side */}
          <div className="section-header">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary-800 dark:text-primary-100 mb-6">
              What Our <span className="text-accent-600 dark:text-accent-400">Clients Say</span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
              Don't just take our word for it. Here's what our satisfied clients have to say about 
              working with Codepact and the results we've delivered.
            </p>
            <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
              Our commitment to excellence and client satisfaction has earned us the trust of businesses 
              across various industries. From startups to enterprise-level organizations, we've consistently 
              delivered innovative solutions that drive growth and success.
            </p>
            <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              Each testimonial represents a unique partnership where we've transformed challenges into 
              opportunities, helping our clients achieve their digital transformation goals and exceed 
              their business objectives.
            </p>
          </div>

          {/* Testimonials Carousel - Right Side */}
          <div ref={testimonialsRef} className="relative">
          {/* Main Testimonial */}
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-800 dark:to-gray-700 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-10 left-10 w-32 h-32 bg-primary-600 rounded-full"></div>
              <div className="absolute bottom-10 right-10 w-24 h-24 bg-primary-600 rounded-full"></div>
              <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-primary-600 rounded-full"></div>
            </div>

            <div className="relative z-10">
            

              {/* Testimonial Content */}
              <blockquote className="text-lg md:text-xl font-medium text-gray-900 dark:text-white mb-6 leading-relaxed">
                "{testimonials[currentTestimonial].content}"
              </blockquote>

              {/* Rating */}
              <div className="flex justify-center mb-6">
                {renderStars(testimonials[currentTestimonial].rating)}
              </div>

              {/* Author */}
              <div className="flex items-center justify-center space-x-4">
                <img
                  src={testimonials[currentTestimonial].image}
                  alt={testimonials[currentTestimonial].name}
                  className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg"
                />
                <div className="text-left">
                  <div className="font-bold text-gray-900 dark:text-white text-lg">
                    {testimonials[currentTestimonial].name}
                  </div>
                  <div className="text-primary-600 dark:text-accent-400 font-medium">
                    {testimonials[currentTestimonial].role}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevTestimonial}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-accent-400 hover:shadow-xl transition-all duration-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={nextTestimonial}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white dark:bg-gray-800 rounded-full shadow-lg flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-accent-400 hover:shadow-xl transition-all duration-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentTestimonial 
                    ? 'bg-primary-600 dark:bg-accent-500 scale-125' 
                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
          </div>
        </div>

        {/* Stats Section Separator */}
        <div className="mt-20 mb-14">
          <div className="h-px bg-gradient-to-r from-transparent via-primary-300 dark:via-primary-600 to-transparent"></div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="text-4xl font-bold gradient-text mb-2">98%</div>
            <div className="text-gray-600 dark:text-gray-300 font-medium">Client Satisfaction</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold gradient-text mb-2">500+</div>
            <div className="text-gray-600 dark:text-gray-300 font-medium">Projects Delivered</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold gradient-text mb-2">200+</div>
            <div className="text-gray-600 dark:text-gray-300 font-medium">Happy Clients</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold gradient-text mb-2">24/7</div>
            <div className="text-gray-600 dark:text-gray-300 font-medium">Support Available</div>
          </div>
        </div>
      </div>
      {/* Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-300 dark:via-primary-600 to-transparent"></div>
    </section>
  );
};

export default Testimonials;
